import axios from "@/lib/axios";

// Types for onboarding (simplified, expand as needed)
export interface CompletionBreakdown {
  name: string;
  weight: number;
  completion: number;
  completedFields: number;
  totalFields: number;
}

export interface Onboarding {
  _id?: string;
  candidate: any;
  status: string;
  companyEmail: string;
  previousEmployerDocuments?: {
    expLetterUrl: string;
    relievingLetterUrl: string;
    resignationAcceptanceUrl: string;
    firstMonthSalarySlipUrl: string;
    secondMonthSalarySlipUrl: string;
    thirdMonthSalarySlipUrl: string;
  };
  identityProofs: {
    aadhaarNumber: string;
    aadhaarDocUrl: string;
    panNumber: string;
    panDocUrl: string;
  };
  educationalCertificates?: Array<{
    degree: string;
    institution: string;
    docUrl: string;
  }>;
  tasks?: string[];
  completionPercentage?: number;
  completionBreakdown?: CompletionBreakdown[];
  notes?: string;
  createdBy?: any;
  updatedBy?: any;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const BASE_URL = "/onboarding";

export const getAllOnboardings = (params?: any) =>
  axios.get(BASE_URL, { params });

export const getOnboardingById = (id: string) => axios.get(`${BASE_URL}/${id}`);

export const createOnboarding = async (formData: FormData) => {
  try {
    // Create a new FormData for the backend
    const backendFormData = new FormData();

    // Extract data from the original FormData
    const data: any = {};
    const files: {
      aadhaarDoc?: File;
      panDoc?: File;
      educationalCertificates?: File[];
      expLetter?: File;
      relievingLetter?: File;
      resignationAcceptance?: File;
      firstMonthSalarySlip?: File;
      secondMonthSalarySlip?: File;
      thirdMonthSalarySlip?: File;
    } = {};

    // Use a different approach to iterate through FormData
    const formDataArray = Array.from(formData.entries() || []);
    for (const [key, value] of formDataArray) {
      if (value instanceof File) {
        // Handle file uploads
        if (key === "identityProofs.aadhaarDocUrl") {
          files.aadhaarDoc = value;
        } else if (key === "identityProofs.panDocUrl") {
          files.panDoc = value;
        } else if (
          key.startsWith("educationalCertificates.") &&
          key.endsWith(".docUrl")
        ) {
          if (!files.educationalCertificates) {
            files.educationalCertificates = [];
          }
          files.educationalCertificates.push(value);
        } else if (key === "previousEmployerDocuments.expLetterUrl") {
          files.expLetter = value;
        } else if (key === "previousEmployerDocuments.relievingLetterUrl") {
          files.relievingLetter = value;
        } else if (
          key === "previousEmployerDocuments.resignationAcceptanceUrl"
        ) {
          files.resignationAcceptance = value;
        } else if (
          key === "previousEmployerDocuments.firstMonthSalarySlipUrl"
        ) {
          files.firstMonthSalarySlip = value;
        } else if (
          key === "previousEmployerDocuments.secondMonthSalarySlipUrl"
        ) {
          files.secondMonthSalarySlip = value;
        } else if (
          key === "previousEmployerDocuments.thirdMonthSalarySlipUrl"
        ) {
          files.thirdMonthSalarySlip = value;
        }
      } else {
        // Handle data fields
        // Check if this is the 'data' field containing JSON
        if (key === "data") {
          try {
            const jsonData = JSON.parse(value as string);

            // Extract candidateId from the JSON data
            if (jsonData.candidateId) {
              data.candidateId = jsonData.candidateId;
            }

            // Copy all other fields from JSON data
            Object.keys(jsonData).forEach((jsonKey) => {
              if (jsonKey !== "candidateId") {
                data[jsonKey] = jsonData[jsonKey];
              }
            });
          } catch (parseError) {
            console.error("Error parsing JSON data:", parseError);
            // Fallback to treating as string
            data[key] = value as string;
          }
        } else {
          // Handle individual fields (fallback)
          if (key === "candidateId" || key === "candidate") {
            data.candidateId = value as string;
          } else if (key === "companyEmail") {
            data.companyEmail = value as string;
          } else if (key === "identityProofs") {
            try {
              data.identityProofs = JSON.parse(value as string);
            } catch {
              data.identityProofs = value as string;
            }
          } else if (key === "previousEmployerDocuments") {
            try {
              data.previousEmployerDocuments = JSON.parse(value as string);
            } catch {
              data.previousEmployerDocuments = value as string;
            }
          } else if (key === "educationalCertificates") {
            try {
              data.educationalCertificates = JSON.parse(value as string);
            } catch {
              data.educationalCertificates = value as string;
            }
          } else if (key === "tasks") {
            try {
              data.tasks = JSON.parse(value as string);
            } catch {
              data.tasks = value as string;
            }
          } else if (key === "notes") {
            data.notes = value as string;
          }
        }
      }
    }

    // Validate that we have a candidateId
    if (!data.candidateId) {
      console.error("No candidateId found in the data");
      throw new Error("Candidate ID is required for creating onboarding");
    }

    // Clean up the data object - remove empty fields
    const cleanData: any = {};
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
        if (typeof data[key] === "object" && !Array.isArray(data[key])) {
          // For nested objects, only include if they have at least one non-empty value
          const nestedObj = data[key];
          const hasValues = Object.values(nestedObj).some(
            (val) => val !== undefined && val !== null && val !== ""
          );
          if (hasValues) {
            cleanData[key] = nestedObj;
          }
        } else if (Array.isArray(data[key])) {
          // For arrays, only include if they have at least one item
          if (data[key].length > 0) {
            cleanData[key] = data[key];
          }
        } else {
          cleanData[key] = data[key];
        }
      }
    });

    // Add the data as a JSON string - this is what the backend expects
    backendFormData.append("data", JSON.stringify(cleanData));

    // Add files with the correct field names expected by the backend
    if (files.aadhaarDoc) {
      backendFormData.append("aadhaarDoc", files.aadhaarDoc);
    }
    if (files.panDoc) {
      backendFormData.append("panDoc", files.panDoc);
    }
    if (files.educationalCertificates) {
      files.educationalCertificates.forEach((file) => {
        backendFormData.append("educationalCertificates", file);
      });
    }
    // Add previous employer document files
    if (files.expLetter) {
      backendFormData.append("expLetter", files.expLetter);
    }
    if (files.relievingLetter) {
      backendFormData.append("relievingLetter", files.relievingLetter);
    }
    if (files.resignationAcceptance) {
      backendFormData.append(
        "resignationAcceptance",
        files.resignationAcceptance
      );
    }
    if (files.firstMonthSalarySlip) {
      backendFormData.append(
        "firstMonthSalarySlip",
        files.firstMonthSalarySlip
      );
    }
    if (files.secondMonthSalarySlip) {
      backendFormData.append(
        "secondMonthSalarySlip",
        files.secondMonthSalarySlip
      );
    }
    if (files.thirdMonthSalarySlip) {
      backendFormData.append(
        "thirdMonthSalarySlip",
        files.thirdMonthSalarySlip
      );
    }

    const response = await axios.post("/onboarding", backendFormData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000, // 60 second timeout for file uploads
    });

    // Automatically assign all available tasks to the new onboarding record
    try {
      const newOnboarding = response.data?.data || response.data;
      if (newOnboarding && newOnboarding._id) {
        // Get all available onboarding tasks
        const tasksResponse = await axios.get("/onboarding-tasks");
        const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

        if (allTasks.length > 0) {

          // Assign all tasks to the new onboarding
          // await updateOnboardingTasks(newOnboarding._id, taskIds);

          // Recalculate completion percentage
          await calculateAndUpdateCompletion(newOnboarding._id);
        }
      }
    } catch (error) {
      console.error(
        "Error automatically assigning tasks to new onboarding:",
        error
      );
      // Don't throw error here as the onboarding was created successfully
    }

    return response.data;
  } catch (error: any) {
    console.error("Onboarding service error:", error);

    // Log detailed error information
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);

      // Throw a more descriptive error
      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`;
      throw new Error(errorMessage);
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error(
        "No response received from server. The request may have timed out."
      );
    } else if (error.code === "ECONNABORTED") {
      console.error("Request timeout:", error);
      throw new Error("Request timed out. Please try again.");
    } else {
      console.error("Error message:", error.message);
      throw new Error(error.message);
    }
  }
};

export const updateOnboarding = async (id: string, data: any) => {
  try {
    // Debug logging

    // Check if there are any files to upload
    const hasFiles =
      data.fileUrls &&
      Object.values(data.fileUrls).some((value) => value instanceof File);

    if (hasFiles) {
      // If there are files, use FormData approach
      const backendFormData = new FormData();

      // Extract data and files
      const cleanData: any = {};
      const files: {
        aadhaarDoc?: File;
        panDoc?: File;
        educationalCertificates?: File[];
        expLetter?: File;
        relievingLetter?: File;
        resignationAcceptance?: File;
        firstMonthSalarySlip?: File;
        secondMonthSalarySlip?: File;
        thirdMonthSalarySlip?: File;
      } = {};

      // Process the data object - EXCLUDE candidateId and candidate from updates
      if (data.companyEmail) {
        cleanData.companyEmail = data.companyEmail;
      }
      if (data.identityProofs) {
        cleanData.identityProofs = data.identityProofs;
      }
      if (data.previousEmployerDocuments) {
        // Always include previousEmployerDocuments if it exists in the data
        // This ensures existing URLs are preserved
        cleanData.previousEmployerDocuments = data.previousEmployerDocuments;
      }
      if (
        data.educationalCertificates &&
        Array.isArray(data.educationalCertificates)
      ) {
        cleanData.educationalCertificates = data.educationalCertificates;
      }
      if (data.tasks && Array.isArray(data.tasks)) {
        cleanData.tasks = data.tasks;
      }
      if (data.notes) {
        cleanData.notes = data.notes;
      }

      // Handle file uploads
      if (data.fileUrls) {
        if (data.fileUrls["identityProofs.aadhaarDocUrl"] instanceof File) {
          files.aadhaarDoc = data.fileUrls["identityProofs.aadhaarDocUrl"];
        }
        if (data.fileUrls["identityProofs.panDocUrl"] instanceof File) {
          files.panDoc = data.fileUrls["identityProofs.panDocUrl"];
        }
        if (
          data.educationalCertificates &&
          Array.isArray(data.educationalCertificates)
        ) {
          data.educationalCertificates.forEach((_cert: any, index: number) => {
            const certFile =
              data.fileUrls[`educationalCertificates.${index}.docUrl`];
            if (certFile instanceof File) {
              if (!files.educationalCertificates) {
                files.educationalCertificates = [];
              }
              files.educationalCertificates.push(certFile);
            }
          });
        }
        // Handle previous employer document files
        if (
          data.fileUrls["previousEmployerDocuments.expLetterUrl"] instanceof
          File
        ) {
          files.expLetter =
            data.fileUrls["previousEmployerDocuments.expLetterUrl"];
        }
        if (
          data.fileUrls[
            "previousEmployerDocuments.relievingLetterUrl"
          ] instanceof File
        ) {
          files.relievingLetter =
            data.fileUrls["previousEmployerDocuments.relievingLetterUrl"];
        }
        if (
          data.fileUrls[
            "previousEmployerDocuments.resignationAcceptanceUrl"
          ] instanceof File
        ) {
          files.resignationAcceptance =
            data.fileUrls["previousEmployerDocuments.resignationAcceptanceUrl"];
        }
        if (
          data.fileUrls[
            "previousEmployerDocuments.firstMonthSalarySlipUrl"
          ] instanceof File
        ) {
          files.firstMonthSalarySlip =
            data.fileUrls["previousEmployerDocuments.firstMonthSalarySlipUrl"];
        }
        if (
          data.fileUrls[
            "previousEmployerDocuments.secondMonthSalarySlipUrl"
          ] instanceof File
        ) {
          files.secondMonthSalarySlip =
            data.fileUrls["previousEmployerDocuments.secondMonthSalarySlipUrl"];
        }
        if (
          data.fileUrls[
            "previousEmployerDocuments.thirdMonthSalarySlipUrl"
          ] instanceof File
        ) {
          files.thirdMonthSalarySlip =
            data.fileUrls["previousEmployerDocuments.thirdMonthSalarySlipUrl"];
        }
      }

      // Clean up the data object - remove empty fields but preserve existing URLs
      const finalData: any = {};
      Object.keys(cleanData).forEach((key) => {
        if (cleanData[key] !== undefined && cleanData[key] !== null) {
          if (
            typeof cleanData[key] === "object" &&
            !Array.isArray(cleanData[key])
          ) {
            // For nested objects, always include if they exist in the input data
            // This allows intentional removals to be processed (even if all values are null)
            const nestedObj = cleanData[key];
            if (Object.keys(nestedObj).length > 0) {
              // Convert empty strings to null for document removals
              const processedObj = { ...nestedObj };
              Object.keys(processedObj).forEach((objKey) => {
                if (processedObj[objKey] === "") {
                  processedObj[objKey] = null;
                }
              });
              finalData[key] = processedObj;
            }
          } else if (Array.isArray(cleanData[key])) {
            // For arrays, only include if they have at least one item
            if (cleanData[key].length > 0) {
              finalData[key] = cleanData[key];
            }
          } else {
            finalData[key] = cleanData[key];
          }
        }
      });

      // EXCLUDE tasks from general update - tasks should be updated through separate endpoint
      if (finalData.tasks) {
        delete finalData.tasks;
      }

      // Add the data as a JSON string - this is what the backend expects
      backendFormData.append("data", JSON.stringify(finalData));

      // Add files with the correct field names expected by the backend
      if (files.aadhaarDoc) {
        backendFormData.append("aadhaarDoc", files.aadhaarDoc);
      }
      if (files.panDoc) {
        backendFormData.append("panDoc", files.panDoc);
      }
      if (files.educationalCertificates) {
        files.educationalCertificates.forEach((file) => {
          backendFormData.append("educationalCertificates", file);
        });
      }
      // Add previous employer document files
      if (files.expLetter) {
        backendFormData.append("expLetter", files.expLetter);
      }
      if (files.relievingLetter) {
        backendFormData.append("relievingLetter", files.relievingLetter);
      }
      if (files.resignationAcceptance) {
        backendFormData.append(
          "resignationAcceptance",
          files.resignationAcceptance
        );
      }
      if (files.firstMonthSalarySlip) {
        backendFormData.append(
          "firstMonthSalarySlip",
          files.firstMonthSalarySlip
        );
      }
      if (files.secondMonthSalarySlip) {
        backendFormData.append(
          "secondMonthSalarySlip",
          files.secondMonthSalarySlip
        );
      }
      if (files.thirdMonthSalarySlip) {
        backendFormData.append(
          "thirdMonthSalarySlip",
          files.thirdMonthSalarySlip
        );
      }

      const response = await axios.patch(`${BASE_URL}/${id}`, backendFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Add response logging

      return response.data;
    } else {
      // If no files, use regular JSON update
      const updateData: any = {};

      // Process the data object - EXCLUDE candidateId and candidate from updates
      if (data.companyEmail) {
        updateData.companyEmail = data.companyEmail;
      }
      if (data.identityProofs) {
        updateData.identityProofs = data.identityProofs;
      }
      if (data.previousEmployerDocuments) {
        // Always include previousEmployerDocuments if it exists in the data
        // This ensures existing URLs are preserved
        updateData.previousEmployerDocuments = data.previousEmployerDocuments;
      }
      if (
        data.educationalCertificates &&
        Array.isArray(data.educationalCertificates)
      ) {
        updateData.educationalCertificates = data.educationalCertificates;
      }
      if (data.tasks && Array.isArray(data.tasks)) {
        updateData.tasks = data.tasks;
      }
      if (data.notes) {
        updateData.notes = data.notes;
      }

      // Clean up the data object - preserve blank fields as they indicate intentional removals
      const finalData: any = {};
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] !== undefined && updateData[key] !== null) {
          if (
            typeof updateData[key] === "object" &&
            !Array.isArray(updateData[key])
          ) {
            // For nested objects, always include if they exist in the input data
            // This allows intentional removals to be processed (even if all values are empty strings)
            const nestedObj = updateData[key];
            if (Object.keys(nestedObj).length > 0) {
              // Convert empty strings to null for document removals (same as FormData approach)
              const processedObj = { ...nestedObj };
              Object.keys(processedObj).forEach((objKey) => {
                if (processedObj[objKey] === "") {
                  processedObj[objKey] = null;
                }
              });
              finalData[key] = processedObj;
            }
          } else if (Array.isArray(updateData[key])) {
            // For arrays, only include if they have at least one item
            if (updateData[key].length > 0) {
              finalData[key] = updateData[key];
            }
          } else {
            finalData[key] = updateData[key];
          }
        }
      });

      // EXCLUDE tasks from general update - tasks should be updated through separate endpoint
      if (finalData.tasks) {
        delete finalData.tasks;
      }

      // IMPORTANT: The backend expects the data to be wrapped in a 'data' field
      // This is the key issue - the backend controller expects the data in a specific format
      const requestData = {
        data: JSON.stringify(finalData),
      };

      const response = await axios.patch(`${BASE_URL}/${id}`, requestData);

      // Add response logging

      return response.data;
    }
  } catch (error: any) {
    console.error("Error updating onboarding:", error);
    console.error("Error response:", error.response);
    console.error("Error message:", error.message);
    throw new Error(
      error.response?.data?.message || "Failed to update onboarding"
    );
  }
};

export const deleteOnboarding = (id: string) =>
  axios.delete(`${BASE_URL}/${id}`);

// Add function to update onboarding tasks
export const updateOnboardingTasks = async (
  onboardingId: string,
  taskIds: string[]
) => {
  try {
    const response = await axios.put(`${BASE_URL}/${onboardingId}/tasks`, {
      taskIds: taskIds,
    });

    return response.data;
  } catch (error) {
    console.error("Error updating onboarding tasks:", error);
    throw error;
  }
};

// Function to add specific tasks to an existing onboarding
export const addTasksToOnboarding = async (
  onboardingId: string,
  taskIds: string[]
) => {
  try {
    // First, get the current onboarding to see existing tasks
    const onboardingResponse = await axios.get(`${BASE_URL}/${onboardingId}`);
    const onboarding = onboardingResponse.data?.data || onboardingResponse.data;

    if (!onboarding) {
      throw new Error("Onboarding not found");
    }

    // Get current tasks and add new ones, avoiding duplicates
    const currentTasks = onboarding.tasks || [];
    const newTaskIds = taskIds.filter(id => !currentTasks.includes(id));
    
    if (newTaskIds.length === 0) {
      return { message: "All selected tasks are already assigned to this onboarding" };
    }

    // Combine existing and new tasks
    const updatedTaskIds = [...currentTasks, ...newTaskIds];

    // Update the onboarding with the new task list
    const response = await axios.put(`${BASE_URL}/${onboardingId}/tasks`, {
      taskIds: updatedTaskIds,
    });

    return response.data;
  } catch (error) {
    console.error("Error adding tasks to onboarding:", error);
    throw error;
  }
};

// Function to calculate timeline completion percentage
export const calculateTimelineCompletion = (onboarding: any) => {
  const timelineItems = [
    // Documentation Collection
    {
      completed: !!(
        onboarding.identityProofs?.aadhaarNumber &&
        onboarding.identityProofs?.panNumber
      ),
      weight: 20,
    },
    // Previous Employer Documents
    {
      completed: !!(
        onboarding.previousEmployerDocuments?.expLetterUrl &&
        onboarding.previousEmployerDocuments?.relievingLetterUrl
      ),
      weight: 25,
    },
    // Educational Certificates
    {
      completed: !!(
        onboarding.educationalCertificates &&
        onboarding.educationalCertificates.length > 0
      ),
      weight: 20,
    },
    // Onboarding Tasks
    {
      completed: false, // This will be calculated separately based on task instances
      weight: 15,
    },
  ];

  // Calculate completion for timeline items (excluding tasks)
  const nonTaskItems = timelineItems.slice(0, 3);
  const nonTaskCompletion = nonTaskItems.reduce((total, item) => {
    return total + (item.completed ? item.weight : 0);
  }, 0);

  // For tasks, we need to get task instances to calculate completion
  // This will be handled in the main calculation function
  return {
    nonTaskCompletion,
    totalNonTaskWeight: nonTaskItems.reduce(
      (total, item) => total + item.weight,
      0
    ),
    timelineItems,
  };
};

export const calculateAndUpdateCompletion = async (onboardingId: string) => {
  try {
    // Get the onboarding record
    const response = await axios.get(`${BASE_URL}/${onboardingId}`);
    const onboarding = response.data?.data || response.data;

    if (!onboarding) {
      console.error("Onboarding not found:", onboardingId);
      return null;
    }

    // Calculate timeline completion
    const timelineCompletion = calculateTimelineCompletion(onboarding);

    // Calculate task completion by fetching task instances
    let taskCompletionPercentage = 0;
    let totalTasks = 0;
    let completedTasks = 0;
    let taskInstances: any[] = [];

    if (
      onboarding.tasks &&
      Array.isArray(onboarding.tasks) &&
      onboarding.tasks.length > 0
    ) {
      try {
        // Get task instances with their actual status
        taskInstances = await getOnboardingTaskInstances(onboardingId);

        totalTasks = taskInstances.length;
        completedTasks = taskInstances.filter(
          (task: any) => task.status === "completed"
        ).length;

        taskCompletionPercentage =
          totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      } catch (error) {
        console.error(
          "Error fetching task instances for completion calculation:",
          error
        );
        // Fallback: assume all tasks are pending if we can't fetch instances
        taskCompletionPercentage = 0;
      }
    }

    // Calculate timeline completion percentage (excluding tasks)
    const timelineCompletionPercentage =
      timelineCompletion.totalNonTaskWeight > 0
        ? Math.round(
            (timelineCompletion.nonTaskCompletion /
              timelineCompletion.totalNonTaskWeight) *
              100
          )
        : 0;

    // Calculate overall completion percentage
    // Timeline items (excluding tasks): 85% weight
    // Tasks: 15% weight
    const timelineWeight = 85;
    const taskWeight = 15;

    // Calculate weighted completion
    const timelineContribution =
      (timelineCompletionPercentage * timelineWeight) / 100;
    const taskContribution = (taskCompletionPercentage * taskWeight) / 100;

    // Calculate overall completion
    const overallCompletionPercentage = Math.round(
      timelineContribution + taskContribution
    );

    // Prepare update data
    const updateData: any = {
      completionPercentage: overallCompletionPercentage,
    };

    // Determine the correct status based on completion
    let newStatus = onboarding.status;

    // Check task status first to determine overall status
    if (totalTasks > 0) {
      const pendingTasks = taskInstances.filter(
        (task: any) => !task.status || task.status === "pending"
      ).length;
      const inProgressTasks = taskInstances.filter(
        (task: any) => task.status === "in_progress"
      ).length;
      const completedTasks = taskInstances.filter(
        (task: any) => task.status === "completed"
      ).length;

      // If there are pending tasks, status should be pending
      if (pendingTasks > 0) {
        newStatus = "pending";
      }
      // If there are in-progress tasks, status should be in_progress
      else if (inProgressTasks > 0) {
        newStatus = "in_progress";
      }
      // If all tasks are completed, check if timeline is also complete
      else if (completedTasks === totalTasks) {
        if (timelineCompletionPercentage === 100) {
          newStatus = "completed";
        } else {
          newStatus = "in_progress";
        }
      }
    } else {
      // No tasks assigned, check timeline completion
      if (timelineCompletionPercentage === 100) {
        newStatus = "completed";
      } else if (overallCompletionPercentage > 0) {
        newStatus = "in_progress";
      } else {
        newStatus = "pending";
      }
    }

    // Update status if it has changed
    if (newStatus !== onboarding.status) {
      updateData.status = newStatus;
    }

    // Update the onboarding record
    if (Object.keys(updateData).length > 0) {
      const updateResponse = await axios.patch(`${BASE_URL}/${onboardingId}`, {
        data: JSON.stringify(updateData),
      });

      return updateResponse.data;
    }

    return null;
  } catch (error) {
    throw error;
  }
};

// Function to update a specific task status within an onboarding
export const updateOnboardingTaskStatus = async (
  onboardingId: string,
  taskInstanceId: string,
  status: string
) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/${onboardingId}/tasks/${taskInstanceId}`,
      {
        status: status,
      }
    );

    // After updating task status, recalculate completion percentage
    await calculateAndUpdateCompletion(onboardingId);

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to automatically assign default onboarding tasks when candidate is hired
export const assignDefaultOnboardingTasks = async (candidateId: string) => {
  try {
    // First, get all available onboarding tasks
    const tasksResponse = await axios.get("/onboarding-tasks");
    const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

    if (allTasks.length === 0) {
      return null;
    }

    // Get all task IDs (we'll assign all available tasks by default)
    const taskIds = allTasks.map((task: any) => task._id || task.id);

    // Find the onboarding record for this candidate
    const onboardingsResponse = await axios.get("/onboarding", {
      params: { search: candidateId, limit: 1 },
    });

    const onboardings =
      onboardingsResponse.data?.data || onboardingsResponse.data || [];
    const onboarding = onboardings.find(
      (onb: any) =>
        onb.candidate?._id === candidateId ||
        onb.candidate?.id === candidateId ||
        onb.candidate === candidateId
    );

    if (!onboarding) {
      return null;
    }

    // Assign the tasks to the onboarding
    const result = await updateOnboardingTasks(onboarding._id, taskIds);

    return result;
  } catch (error) {
    throw error;
  }
};

// Function to create onboarding record and assign tasks for hired candidate
export const createOnboardingForHiredCandidate = async (
  candidateId: string
) => {
  try {
    // First, get candidate details
    const candidateResponse = await axios.get(`/candidates/${candidateId}`);
    const candidate = candidateResponse.data?.data || candidateResponse.data;

    if (!candidate) {
      return null;
    }

    // Check if onboarding already exists
    const onboardingsResponse = await axios.get("/onboarding", {
      params: { search: candidateId, limit: 1 },
    });

    const onboardings =
      onboardingsResponse.data?.data || onboardingsResponse.data || [];
    const existingOnboarding = onboardings.find(
      (onb: any) =>
        onb.candidate?._id === candidateId ||
        onb.candidate?.id === candidateId ||
        onb.candidate === candidateId
    );

    if (existingOnboarding) {
      // Assign tasks to existing onboarding
      return await assignDefaultOnboardingTasks(candidateId);
    }

    // Create new onboarding record
    const onboardingData = new FormData();
    onboardingData.append(
      "data",
      JSON.stringify({
        candidateId: candidateId,
        companyEmail: `${candidate.firstName?.toLowerCase()}.${candidate.lastName?.toLowerCase()}@company.com`,
        status: "pending",
      })
    );

    await axios.post("/onboarding", onboardingData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // Assign default tasks to the new onboarding
    const result = await assignDefaultOnboardingTasks(candidateId);

    return result;
  } catch (error) {
    throw error;
  }
};

// Function to manually assign tasks to existing onboarding records
export const assignTasksToExistingOnboarding = async (onboardingId: string) => {
  try {
    // First, check if onboarding exists
    const onboardingResponse = await axios.get(`${BASE_URL}/${onboardingId}`);
    const onboarding = onboardingResponse.data?.data || onboardingResponse.data;

    if (!onboarding) {
      console.error("Onboarding not found:", onboardingId);
      throw new Error("Onboarding not found");
    }

    // Get all available onboarding tasks
    const tasksResponse = await axios.get("/onboarding-tasks");
    const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

    if (allTasks.length === 0) {
      return null;
    }

    // Get all task IDs
    const taskIds = allTasks.map((task: any) => task._id || task.id);

    // Assign tasks to the onboarding
    const result = await updateOnboardingTasks(onboardingId, taskIds);

    return result;
  } catch (error) {
    throw error;
  }
};

// Function to get onboarding tasks for a specific onboarding
export const getOnboardingTasks = async (onboardingId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/${onboardingId}`);
    const onboarding = response.data?.data || response.data;

    if (!onboarding) {
      return [];
    }

    return onboarding.tasks || [];
  } catch (error) {
    throw error;
  }
};

// Function to get onboarding task instances for a specific onboarding
export const getOnboardingTaskInstances = async (onboardingId: string) => {
  try {
    // Get the onboarding record which should contain the tasks
    const response = await axios.get(`${BASE_URL}/${onboardingId}`);
    const onboarding = response.data?.data || response.data;

    if (!onboarding) {
      return [];
    }

    // If tasks are embedded in the onboarding record, process them
    if (
      onboarding.tasks &&
      Array.isArray(onboarding.tasks) &&
      onboarding.tasks.length > 0
    ) {
      // Check if tasks are just IDs (strings) or full objects
      const hasTaskIdsOnly = onboarding.tasks.every(
        (task: any) => typeof task === "string"
      );

      if (hasTaskIdsOnly) {
        // Fetch full task details for each task ID
        const taskDetails = [];

        for (const taskId of onboarding.tasks) {
          try {
            const taskResponse = await axios.get(`/onboarding-tasks/${taskId}`);
            const taskData = taskResponse.data?.data || taskResponse.data;

            if (taskData) {
              const taskInstance = {
                _id: taskId, // Use the task ID as instance ID
                taskId: taskId, // Original task template ID
                title: taskData.title,
                description: taskData.description,
                category: taskData.category,
                dueDate: taskData.dueDate,
                status: "pending", // Default status for new task instances
                weight: taskData.weight || 1,
                isActive: taskData.isActive !== false,
              };
              taskDetails.push(taskInstance);
            }
          } catch (error) {
            // Add a fallback task object
            const fallbackTask = {
              _id: taskId,
              taskId: taskId,
              title: `Task ${taskId}`,
              description: "Task details not available",
              status: "pending",
            };
            taskDetails.push(fallbackTask);
          }
        }

        return taskDetails;
      } else {
        // Handle the actual task structure where each task is an object with taskTemplate
        const taskInstances = onboarding.tasks.map(
          (taskInstance: any, _index: number) => {
            if (
              taskInstance &&
              typeof taskInstance === "object" &&
              taskInstance.taskTemplate
            ) {
              const processedTask = {
                _id: taskInstance._id, // Task instance ID
                taskId: taskInstance.taskTemplate._id, // Original task template ID
                title: taskInstance.taskTemplate.title,
                description: taskInstance.taskTemplate.description,
                category: taskInstance.taskTemplate.category,
                dueDate: taskInstance.taskTemplate.dueDate,
                status: taskInstance.status || "pending",
                weight: taskInstance.taskTemplate.weight,
                isActive: taskInstance.taskTemplate.isActive,
              };
              return processedTask;
            } else if (typeof taskInstance === "string") {
              // Handle case where task is just a string ID (fallback)
              const fallbackTask = {
                _id: taskInstance,
                taskId: taskInstance,
                title: `Task ${taskInstance}`,
                description: "Task details not available",
                status: "pending",
              };
              return fallbackTask;
            } else {
              // Handle unknown structure
              const unknownTask = {
                _id: taskInstance?._id || "unknown",
                taskId: taskInstance?.taskId || "unknown",
                title:
                  taskInstance?.title ||
                  `Task ${taskInstance?._id || "unknown"}`,
                description:
                  taskInstance?.description || "Task details not available",
                status: taskInstance?.status || "pending",
              };
              return unknownTask;
            }
          }
        );

        return taskInstances;
      }
    }

    // If no tasks found, return empty array
    return [];
  } catch (error) {
    throw error;
  }
};

// Utility function to assign tasks to all onboarding records that don't have tasks
export const assignTasksToAllEmptyOnboardings = async () => {
  try {
    // Get all onboarding records
    const onboardingsResponse = await axios.get("/onboarding");
    const onboardings =
      onboardingsResponse.data?.data || onboardingsResponse.data || [];

    // Filter onboarding records that don't have tasks
    const emptyOnboardings = onboardings.filter(
      (onboarding: any) => !onboarding.tasks || onboarding.tasks.length === 0
    );

    if (emptyOnboardings.length === 0) {
      return {
        success: true,
        message: "No onboarding records need task assignment",
      };
    }

    // Get all available onboarding tasks
    const tasksResponse = await axios.get("/onboarding-tasks");
    const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

    if (allTasks.length === 0) {
      return {
        success: false,
        message: "No onboarding tasks available to assign",
      };
    }

    // Get all task IDs
    const taskIds = allTasks.map((task: any) => task._id || task.id);

    // Assign tasks to each empty onboarding
    const results = [];
    for (const onboarding of emptyOnboardings) {
      try {
        const result = await updateOnboardingTasks(onboarding._id, taskIds);
        results.push({ onboardingId: onboarding._id, success: true, result });
      } catch (error: any) {
        results.push({
          onboardingId: onboarding._id,
          success: false,
          error: error?.message || "Unknown error",
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    return {
      success: true,
      message: `Assigned tasks to ${successCount} onboarding records${
        failureCount > 0 ? `, ${failureCount} failed` : ""
      }`,
      results,
    };
  } catch (error) {
    throw error;
  }
};

// Function to calculate completion percentage for all onboardings
export const calculateCompletionForAllOnboardings = async () => {
  try {
    // Get all onboarding records
    const response = await axios.get(`${BASE_URL}`);
    const onboardings = response.data?.data || response.data || [];

    if (!Array.isArray(onboardings)) {
      return null;
    }

    // Process each onboarding
    const results: Array<{
      success: boolean;
      onboarding?: any;
      result?: any;
      error?: string;
    }> = await Promise.all(
      onboardings.map(async (onboarding: any) => {
        try {
          if (!onboarding._id) {
            return { success: false, onboarding: null, error: "No ID" };
          }

          const result = await calculateAndUpdateCompletion(onboarding._id);
          return { success: true, onboarding, result };
        } catch (error: any) {
          return {
            success: false,
            onboarding,
            error: error?.message || "Unknown error",
          };
        }
      })
    );

    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    return {
      success: true,
      message: `Updated completion for ${successCount} onboardings${
        failureCount > 0 ? `, ${failureCount} failed` : ""
      }`,
      results,
    };
  } catch (error: any) {
    throw error;
  }
};

// Function to check and fix task assignments for all onboarding records
export const checkAndFixAllTaskAssignments = async () => {
  try {
    // Get all onboarding tasks
    const tasksResponse = await axios.get("/onboarding-tasks");
    const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

    if (allTasks.length === 0) {
      return { success: false, message: "No tasks found to assign" };
    }

    // Get all onboarding records
    const onboardingsResponse = await axios.get("/onboarding");
    const onboardings =
      onboardingsResponse.data?.data || onboardingsResponse.data || [];

    if (onboardings.length === 0) {
      return { success: false, message: "No onboarding records found" };
    }

    // Get all task IDs
    const taskIds = allTasks.map((task: any) => task._id || task.id);

    // Check and fix each onboarding record
    const results: Array<{
      onboardingId: string;
      success: boolean;
      missingTasks?: number;
      extraTasks?: number;
      finalTasks?: number;
      error?: string;
    }> = [];
    for (const onboarding of onboardings) {
      try {
        // Get current tasks for this onboarding
        const currentTasks = onboarding.tasks || [];

        // Check if all tasks are assigned
        const missingTasks = taskIds.filter(
          (taskId: string) => !currentTasks.includes(taskId)
        );
        const extraTasks = currentTasks.filter(
          (taskId: string) => !taskIds.includes(taskId)
        );

        if (missingTasks.length > 0 || extraTasks.length > 0) {
          // Create a set of all task IDs (current + missing - extra)
          const allTaskIds = Array.from(
            new Set([...currentTasks, ...missingTasks])
          ).filter((taskId) => !extraTasks.includes(taskId));

          // Update the onboarding with all tasks
          await axios.put(`${BASE_URL}/${onboarding._id}/tasks`, {
            taskIds: allTaskIds,
          });

          // Recalculate completion percentage for this onboarding
          try {
            await calculateAndUpdateCompletion(onboarding._id);
          } catch (completionError) {}

          results.push({
            onboardingId: onboarding._id,
            success: true,
            missingTasks: missingTasks.length,
            extraTasks: extraTasks.length,
            finalTasks: allTaskIds.length,
          });
        } else {
          results.push({
            onboardingId: onboarding._id,
            success: true,
            missingTasks: 0,
            extraTasks: 0,
            finalTasks: currentTasks.length,
          });
        }
      } catch (error: any) {
        results.push({
          onboardingId: onboarding._id,
          success: false,
          error: error.message,
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    return {
      success: true,
      message: `Fixed task assignments for ${successCount} onboarding records${
        failureCount > 0 ? `, ${failureCount} failed` : ""
      }`,
      results,
    };
  } catch (error: any) {
    throw error;
  }
};

// Function to delete a file from the backend
export const deleteOnboardingFile = async (fileUrl: string): Promise<void> => {
  try {
    // Use the file upload service to delete the file
    const { fileUploadService } = await import("./fileUpload.service");
    await fileUploadService.deleteFile(fileUrl);
  } catch (error: any) {
    console.error("Error deleting onboarding file:", error);
    // Don't throw error here as file deletion failure shouldn't block the main operation
    // Just log the error for debugging
  }
};

// Function to handle file removal and backend cleanup
export const removeOnboardingFile = async (
  onboardingId: string,
  fileUrl: string,
  fieldPath: string
): Promise<void> => {
  try {
    // First, delete the file from the backend storage
    if (fileUrl && fileUrl.startsWith("http")) {
      try {
        const { fileUploadService } = await import("./fileUpload.service");
        await fileUploadService.deleteFile(fileUrl);
      } catch (deleteError) {
        // Continue with database update even if file deletion fails
      }
    }

    // Then update the onboarding record to remove the file URL reference
    const updateData: any = {};

    // Parse the field path to set the correct nested field to null
    if (fieldPath === "identityProofs.aadhaarDocUrl") {
      updateData.identityProofs = { aadhaarDocUrl: null };
    } else if (fieldPath === "identityProofs.panDocUrl") {
      updateData.identityProofs = { panDocUrl: null };
    } else if (fieldPath.startsWith("educationalCertificates.")) {
      // For educational certificates, we need to update the specific certificate
      const match = fieldPath.match(/educationalCertificates\.(\d+)\.docUrl/);
      if (match) {
        const index = parseInt(match[1]);
        // Get current onboarding to update the specific certificate
        const currentOnboarding = await getOnboardingById(onboardingId);
        const onboarding =
          currentOnboarding.data?.data || currentOnboarding.data;

        if (
          onboarding.educationalCertificates &&
          onboarding.educationalCertificates[index]
        ) {
          const updatedCertificates = [...onboarding.educationalCertificates];
          updatedCertificates[index] = {
            ...updatedCertificates[index],
            docUrl: null,
          };
          updateData.educationalCertificates = updatedCertificates;
        }
      }
    } else if (fieldPath.startsWith("previousEmployerDocuments.")) {
      const fieldName = fieldPath.replace("previousEmployerDocuments.", "");
      // Get the current onboarding to get the current previousEmployerDocuments
      const currentOnboarding = await getOnboardingById(onboardingId);
      const onboarding = currentOnboarding.data?.data || currentOnboarding.data;

      // Get the current previousEmployerDocuments and update only the specific field
      const currentPreviousEmployerDocuments =
        onboarding.previousEmployerDocuments || {};
      const updatedPreviousEmployerDocuments = {
        ...currentPreviousEmployerDocuments,
        [fieldName]: null,
      };
      updateData.previousEmployerDocuments = updatedPreviousEmployerDocuments;
    }

    // Update the onboarding record
    if (Object.keys(updateData).length > 0) {
    }
  } catch (error: any) {
    throw new Error(`Failed to remove file: ${error.message}`);
  }
};

// Function to remove a file from onboarding record (simpler approach)
export const removeFileFromOnboarding = async (
  onboardingId: string,
  fieldPath: string
): Promise<void> => {
  try {
    // First, get the current onboarding to find the file URL
    const currentOnboarding = await getOnboardingById(onboardingId);
    const onboarding = currentOnboarding.data?.data || currentOnboarding.data;

    if (!onboarding) {
      throw new Error("Onboarding not found");
    }

    // Extract the file URL based on the field path
    let fileUrl: string | null = null;

    if (fieldPath === "identityProofs.aadhaarDocUrl") {
      fileUrl = onboarding.identityProofs?.aadhaarDocUrl;
    } else if (fieldPath === "identityProofs.panDocUrl") {
      fileUrl = onboarding.identityProofs?.panDocUrl;
    } else if (fieldPath.startsWith("previousEmployerDocuments.")) {
      const fieldName = fieldPath.replace("previousEmployerDocuments.", "");
      fileUrl =
        onboarding.previousEmployerDocuments?.[
          fieldName as keyof typeof onboarding.previousEmployerDocuments
        ];
    } else if (fieldPath.startsWith("educationalCertificates.")) {
      const match = fieldPath.match(/educationalCertificates\.(\d+)\.docUrl/);
      if (match) {
        const index = parseInt(match[1]);
        fileUrl = onboarding.educationalCertificates?.[index]?.docUrl;
      }
    }

    // Delete the file from storage if it exists
    if (fileUrl && fileUrl.startsWith("http")) {
      try {
        const { fileUploadService } = await import("./fileUpload.service");
        await fileUploadService.deleteFile(fileUrl);
      } catch (deleteError) {
        // Continue with database update even if file deletion fails
      }
    }

    // Prepare the update data based on the field path
    // Use null values to match the working approach in removeOnboardingFile
    let updateData: any = {};

    if (fieldPath === "identityProofs.aadhaarDocUrl") {
      updateData = {
        identityProofs: { aadhaarDocUrl: null },
      };
    } else if (fieldPath === "identityProofs.panDocUrl") {
      updateData = {
        identityProofs: { panDocUrl: null },
      };
    } else if (fieldPath.startsWith("previousEmployerDocuments.")) {
      const fieldName = fieldPath.replace("previousEmployerDocuments.", "");
      // Get the current previousEmployerDocuments and update only the specific field
      const currentPreviousEmployerDocuments =
        onboarding.previousEmployerDocuments || {};
      const updatedPreviousEmployerDocuments = {
        ...currentPreviousEmployerDocuments,
        [fieldName]: null,
      };
      updateData = {
        previousEmployerDocuments: updatedPreviousEmployerDocuments,
      };
    } else if (fieldPath.startsWith("educationalCertificates.")) {
      const match = fieldPath.match(/educationalCertificates\.(\d+)\.docUrl/);
      if (match) {
        const index = parseInt(match[1]);
        // Get current onboarding to update the specific certificate
        if (
          onboarding.educationalCertificates &&
          onboarding.educationalCertificates[index]
        ) {
          const updatedCertificates = [...onboarding.educationalCertificates];
          updatedCertificates[index] = {
            ...updatedCertificates[index],
            docUrl: null,
          };
          updateData = {
            educationalCertificates: updatedCertificates,
          };
        }
      }
    }

    // Send the update in the format the backend expects
    // The backend expects the data as a JSON string in the 'data' field
    const formData = new FormData();
    formData.append("data", JSON.stringify(updateData));

  } catch (error: any) {
    throw new Error(`Failed to remove file: ${error.message}`);
  }
};
